import { UUID } from "crypto";

import { useEffect, useCallback } from "react";

import type { Editor, JSONContent } from "@tiptap/react";
import { debounce, isEqual } from "lodash-es";

import { CONTENT_RESET_META } from "@demo/extensions/utils/node-protection";
import { useDocumentStore } from "@demo/store/document";
import { Role } from "@demo/types/base";

export function useTiptapZustandSync(
  editor: Editor | null,
  documentUuid: UUID | null,
  role: Role,
  isLoadingDocument: UUID | null,
  debounceMs = 200,
): () => void {
  const jsonInStore = useDocumentStore(
    useCallback(
      (s) => {
        if (!documentUuid || !s.draftDocuments) return undefined;
        return s.draftDocuments[documentUuid]?.[role] as JSONContent | undefined;
      },
      [documentUuid, role],
    ),
  );

  useEffect(() => {
    /* We pause syncing while the document is loading to prevent
    the editor from being overwritten with the old document */
    if (!editor || !jsonInStore || isLoadingDocument) return;
    if (!isEqual(editor.getJSON(), jsonInStore)) {
      /* queueMicrotask schedules the setContent work for
      the next micro-task, i.e. after React has finished
      its current commit. TipTap docs recommend following
      this pattern as a standard safety guard */
      queueMicrotask(() => {
        // contentReset exempts this programmatic full-doc swap from node
        // protection; addToHistory keeps it off the user's undo stack.
        editor
          .chain()
          .setMeta(CONTENT_RESET_META, true)
          .setMeta("addToHistory", false)
          .setContent(jsonInStore, false)
          .run();
      });
    }
  }, [editor, jsonInStore, isLoadingDocument, documentUuid]);

  const flush = useCallback(() => {
    if (!editor || !documentUuid) return;
    const { setDraftDocumentJson: setDocumentJson } = useDocumentStore.getState();
    setDocumentJson(documentUuid, role, editor.getJSON());
  }, [editor, documentUuid, role]);

  useEffect(() => {
    if (!editor) return;
    const handler = debounce(flush, debounceMs);
    editor.on("update", handler);
    return () => {
      editor.off("update", handler);
      handler.cancel();
    };
  }, [editor, flush, debounceMs]);

  return flush;
}
